import { type Ref } from "vue";

import { callApi } from "@/api/apiInteraction";
import type { ApiEntry, InventoryItem, StoredUrl } from "@/services/api-inventory/apiInventory.types";
import type { ExcelFunction } from "@/services/custom-functions/customFunction.types";
import { OpenApiExcelFunctionConstructor } from "@/services/custom-functions/openApiExcelFunctionConstructor";
import type { ExtendedDocument } from "@/services/open-api/openApi.types";
import { constructUrl, writeStoredUrls } from "@/utils/utils";

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function createApiInventoryModule(
  baseUrl: Ref<string>,
  rawUrls: Ref<StoredUrl[]>,
  inventory: Ref<InventoryItem[]>,
  apiEntries: Ref<Map<string, ApiEntry>>,
  cachedSchemaMap: Ref<Map<string, ExtendedDocument>>,
  isInventoryLoading: Ref<boolean>,
  inventoryError: Ref<string | null>,
  validateFunctionsForEntry: (url: string, excelFunctions: ExcelFunction[]) => Promise<void>,
  registerAllFunctions: () => Promise<void>
) {
  function setRawUrls(newUrls: StoredUrl[]): void {
    rawUrls.value = writeStoredUrls(newUrls);
  }

  async function loadInventory(): Promise<void> {
    isInventoryLoading.value = true;
    inventoryError.value = null;

    try {
      inventory.value = await callApi<InventoryItem[]>(constructUrl(baseUrl.value, "inventory"));
    } catch (error: unknown) {
      inventoryError.value = getErrorMessage(error, "Failed to load API inventory.");
      console.error("Failed to load API inventory:", error);
    } finally {
      isInventoryLoading.value = false;
    }
  }

  function createApiEntry(url: string, item: StoredUrl): ApiEntry {
    return {
      url,
      item,
      state: "idle",
      error: null,
      schemaInfo: null,
      functions: [],
      registrableFunctions: [],
      nonRegistrableFunctions: [],
      failedEndpoints: [],
      failedEndpointDetails: [],
    };
  }

  async function loadApiSchema(url: string): Promise<void> {
    const entry = apiEntries.value.get(url);
    if (!entry) return;

    apiEntries.value.set(url, { ...entry, state: "loading", error: null });

    try {
      let apiSchema: ExtendedDocument;

      if (cachedSchemaMap.value.has(url)) {
        const cachedSchema = cachedSchemaMap.value.get(url);
        if (!cachedSchema) {
          throw new Error(`Cache miss for ${url} after has() check.`);
        }
        apiSchema = cachedSchema;
      } else {
        apiSchema = await callApi<ExtendedDocument>(constructUrl(url, "openapi.json"));
        cachedSchemaMap.value.set(url, apiSchema);
      }

      const constructor = new OpenApiExcelFunctionConstructor(apiSchema, url);
      const generatedFunctions = constructor.construct();
      const latestEntry = apiEntries.value.get(url);
      if (!latestEntry) {
        return;
      }

      const updatedEntry: ApiEntry = {
        ...latestEntry,
        state: "loaded",
        error: null,
        schemaInfo: apiSchema.info,
        functions: generatedFunctions,
        failedEndpoints: constructor.notSupportedApiEndpoints,
        failedEndpointDetails: constructor.notSupportedApiEndpointDetails,
        registrableFunctions: [],
        nonRegistrableFunctions: [],
      };

      apiEntries.value.set(url, updatedEntry);

      if (generatedFunctions.length > 0) {
        await validateFunctionsForEntry(url, generatedFunctions);
      }
    } catch (error: unknown) {
      console.error(`Failed to load schema for ${url}:`, error);

      const latestEntry = apiEntries.value.get(url);
      if (!latestEntry) {
        return;
      }

      apiEntries.value.set(url, {
        ...latestEntry,
        state: "error",
        error: getErrorMessage(error, `Failed to load schema for ${url}`),
      });
    }
  }

  async function syncUrls(newUrls: StoredUrl[]): Promise<void> {
    const newUrlsSet = new Set(
      newUrls.map((item) =>
        typeof item === "object" && item !== null ? constructUrl(baseUrl.value, item.path) : String(item)
      )
    );

    const toRemove: string[] = [];
    apiEntries.value.forEach((_, url) => {
      if (!newUrlsSet.has(url)) {
        toRemove.push(url);
      }
    });
    toRemove.forEach((url) => apiEntries.value.delete(url));

    const addPromises: Promise<void>[] = [];
    newUrls.forEach((item) => {
      const url = typeof item === "object" && item !== null ? constructUrl(baseUrl.value, item.path) : String(item);
      if (!apiEntries.value.has(url)) {
        apiEntries.value.set(url, createApiEntry(url, item));
        addPromises.push(loadApiSchema(url));
      }
    });

    await Promise.all(addPromises);

    await registerAllFunctions();
  }

  return {
    setRawUrls,
    loadInventory,
    syncUrls,
  };
}
